/**
 * One page-level shell for an action that deserves deliberate attention.
 *
 * This is broader than a "danger zone": deleting a client is destructive,
 * while releasing a suppression is reversible but can resume delivery to a
 * bad mailbox. The shared visual treatment says "read before acting"; the
 * button label and confirmation dialog explain the exact consequence.
 */
import { SectionHeader } from '../common/section-header'
import { useId, type ReactNode } from 'react'
import { DismissibleAlert } from './dismissible-alert'
import { Notice } from '@speaking-coach/ui'
import { cn } from '../components/ui/cn'

export function HighConsequenceActionPanel({
  dangerZone = false,
  title,
  description,
  error,
  onErrorDismiss,
  children,
  className,
}: {
  dangerZone?: boolean
  title: string
  description: ReactNode
  error?: string | null
  onErrorDismiss?: () => void
  children: ReactNode
  className?: string
}): JSX.Element {
  const titleId = useId()

  return (
    <section
      aria-labelledby={titleId}
      data-slot="high-consequence-action"
      className={cn(
        'overflow-hidden rounded-token-card border border-danger/30 bg-surface-card',
        className,
      )}
    >
      {dangerZone && (
        <div className="bg-danger px-6 py-1.5 text-label font-semibold text-on-danger">
          Danger zone
        </div>
      )}
      <div className="p-6">
        <SectionHeader id={titleId} title={title} />
        <div className="mt-1 text-body text-text-secondary">{description}</div>
        {error !== undefined && error !== null &&
          (onErrorDismiss === undefined ? (
            <Notice variant="danger" className="mt-3">
              {error}
            </Notice>
          ) : (
            <DismissibleAlert
              message={error}
              onDismiss={onErrorDismiss}
              className="mb-0 mt-3"
            />
          ))}
        <div className="mt-3">{children}</div>
      </div>
    </section>
  )
}
